import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  Rating,
  RadioGroup,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { modalStyle } from "../../style/Booking.style";
import RadarAnimation from "./RadarAnimation";
import { createDriverCommentUseCase } from "../../usecases/comments/CreateDriverCommentUseCase";
import { getDriverCommentByBookingUseCase } from "../../usecases/comments/GetDriverCommentByBookingUseCase";
import { updateDriverCommentUseCase } from "../../usecases/comments/UpdateDriverCommentUseCase";

const MAX_MAIN_SECONDS = 15 * 60;
const OFFER_SECONDS = 20;

const CarIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 640"
    sx={{
      width: 28,
      height: 28,
      fill: "#0d47a1",
      flexShrink: 0,
    }}
  >
    <path d="M147 170.7L117.2 256L240.1 256L240.1 160L162.2 160C155.4 160 149.3 164.3 147.1 170.7zM48.6 257.9L86.5 149.6C97.8 117.5 128.1 96 162.1 96L360 96C385.2 96 408.9 107.9 424 128L520.2 256.3C587.1 260.5 640 316.1 640 384L640 400C640 435.3 611.3 464 576 464L559.6 464C555.6 508.9 517.9 544 472 544C426.1 544 388.4 508.9 384.4 464L239.7 464C235.7 508.9 198 544 152.1 544C106.2 544 68.5 508.9 64.5 464L64.1 464C28.8 464 .1 435.3 .1 400L.1 320C.1 289.9 20.8 264.7 48.7 257.9zM440 256L372.8 166.4C369.8 162.4 365 160 360 160L288 160L288 256L440 256zM152 496C174.1 496 192 478.1 192 456C192 433.9 174.1 416 152 416C129.9 416 112 433.9 112 456C112 478.1 129.9 496 152 496zM512 456C512 433.9 494.1 416 472 416C449.9 416 432 433.9 432 456C432 478.1 449.9 496 472 496C494.1 496 512 478.1 512 456z" />
  </Box>
);

const RouteLabel = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      minWidth: 24,
      color: "text.primary",
      fontWeight: 800,
      textAlign: "center",
      flexShrink: 0,
      fontSize: 18,
    }}
  >
    {children}
  </Typography>
);

RouteLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

const formatMainCounter = (seconds) => {
  if (seconds < 60) return seconds === 0 ? "0" : `${seconds} s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds === 0
    ? `${minutes} min`
    : `${minutes} min ${remainingSeconds} s`;
};

const parseBookingDateTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;

  const dateMatch = String(dateValue).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const timeMatch = String(timeValue)
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (!dateMatch || !timeMatch) return null;

  const [, day, month, year] = dateMatch;
  const [, hourValue, minute, meridiem] = timeMatch;
  let hour = Number(hourValue);

  if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
  if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;

  const bookingDateTime = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    hour,
    Number(minute),
    0,
  );

  return Number.isNaN(bookingDateTime.getTime()) ? null : bookingDateTime;
};

const getElapsedBookingSeconds = (dateValue, timeValue) => {
  const bookingDateTime = parseBookingDateTime(dateValue, timeValue);
  if (!bookingDateTime) return 0;

  const elapsedSeconds = Math.floor(
    (Date.now() - bookingDateTime.getTime()) / 1000,
  );
  return Math.min(Math.max(elapsedSeconds, 0), MAX_MAIN_SECONDS);
};

const DetailRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value || "-"}
    </Typography>
  </Box>
);

const getSubRouteLabel = (subRoute) =>
  subRoute?.name ??
  subRoute?.address ??
  subRoute?.description ??
  subRoute?.originName ??
  "";

const SubRoutesList = ({ subRoutes }) => {
  const sortedSubRoutes = Array.isArray(subRoutes)
    ? [...subRoutes].sort((firstRoute, secondRoute) => {
        const firstId = Number(firstRoute?.id);
        const secondId = Number(secondRoute?.id);

        if (Number.isNaN(firstId) || Number.isNaN(secondId)) return 0;
        return firstId - secondId;
      })
    : [];

  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Paraderos:
      </Typography>
      {sortedSubRoutes.length > 0 ? (
        <Stack spacing={0.5}>
          {sortedSubRoutes.map((subRoute, index) => (
            <Typography
              key={subRoute?.id ?? `${getSubRouteLabel(subRoute)}-${index}`}
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {index + 1}. {getSubRouteLabel(subRoute) || "-"}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          -
        </Typography>
      )}
    </Box>
  );
};

const DetailCol = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      minWidth: 0,
      py: 0,
      borderRadius: "6px",
      bgcolor: "grey.50",
    }}
  >
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontWeight: 700, flexShrink: 0 }}
    >
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
      {value || "-"}
    </Typography>
  </Box>
);

const CounterLabel = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      minWidth: 52,
      px: 1,
      py: 0.5,
      borderRadius: "6px",
      bgcolor: "grey.100",
      color: "text.primary",
      fontWeight: 700,
      textAlign: "center",
    }}
  >
    {children}
  </Typography>
);

const getServiceStatusLabel = (statusService) => {
  const normalizedStatus = String(statusService || "").toUpperCase();

  if (normalizedStatus === "PENDING" || normalizedStatus === "PENDIENTE") {
    return "Esperando ...";
  }
  if (normalizedStatus === "RUNNING") return "En Ruta";
  if (normalizedStatus === "FINISH") return "Finalizado";

  return "";
};

const getNormalizedServiceStatus = (statusService) => {
  const normalizedStatus = String(statusService || "").toUpperCase();

  if (normalizedStatus === "PENDING" || normalizedStatus === "PENDIENTE") {
    return "PENDING";
  }
  if (normalizedStatus === "RUNNING") return "RUNNING";
  if (normalizedStatus === "FINISH") return "FINISH";

  return "";
};

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? response ?? null;

const OfferCard = ({ offer, onAccept }) => {
  const [secondsLeft, setSecondsLeft] = useState(OFFER_SECONDS);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsLeft((currentSeconds) => Math.max(currentSeconds - 1, 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        borderColor: "divider",
        bgcolor: "background.paper",
        border: "1px solid #000",
      }}
    >
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
        <Stack spacing={1}>
          <DetailCol label="Conductor" value={offer.nameDriver} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
            }}
          >
            <DetailCol label="Precio" value={`${offer.priceOffer} PEN`} />
            <DetailCol label="Tiempo" value={offer.timeArrived} />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 1,
            }}
          >
            <DetailCol label="Placa" value={offer.plate} />
            <DetailCol label="Marca" value={offer.bodywork} />
            <DetailCol label="Color" value={offer.color} />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CounterLabel>{secondsLeft}</CounterLabel>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={() => onAccept(offer)}
            >
              Aceptar
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const OffersModal = ({
  open,
  onClose,
  bookingOfferDetail,
  acceptedDriver,
  offersList,
  cancellationReasons,
  offersListMaxHeight = { xs: "90vh", sm: "95vh" },
  onAcceptCounterOffer = () => {},
  onCancelBooking = () => {},
}) => {
  const [mainCounterSeconds, setMainCounterSeconds] = useState(0);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [openRatingModal, setOpenRatingModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancellingBooking, setIsCancellingBooking] = useState(false);
  const [driverRating, setDriverRating] = useState(0);
  const [driverComment, setDriverComment] = useState("");
  const [ratingError, setRatingError] = useState("");
  const [isLoadingRating, setIsLoadingRating] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [existingDriverCommentId, setExistingDriverCommentId] = useState(null);
  const previousServiceStatusRef = useRef({
    counterBooking: "",
    status: "",
  });
  const driverOffers = Array.isArray(offersList) ? offersList : [];
  const cancelReasonOptions = Array.isArray(cancellationReasons)
    ? cancellationReasons
    : [];
  const hasDriverOffers = driverOffers.length > 0;
  const hasAcceptedDriver =
    bookingOfferDetail?.idDriver != null &&
    bookingOfferDetail.idDriver !== "" &&
    bookingOfferDetail.idDriver !== 0 &&
    bookingOfferDetail.idDriver !== "0";
  const acceptedDriverDetail = {
    name:
      acceptedDriver?.driverName ??
      acceptedDriver?.nameDriver ??
      bookingOfferDetail?.driverName,
    time:
      acceptedDriver?.time ??
      acceptedDriver?.timeArrived ??
      bookingOfferDetail?.driverArrivalTime,
    plate: acceptedDriver?.plate ?? bookingOfferDetail?.driverPlate,
    model: acceptedDriver?.model ?? bookingOfferDetail?.driverModel,
    color: acceptedDriver?.color ?? bookingOfferDetail?.driverColor,
  };
  const normalizedServiceStatus = getNormalizedServiceStatus(
    bookingOfferDetail?.statusService,
  );
  const isTripPending = normalizedServiceStatus === "PENDING";
  const isTripRunning = normalizedServiceStatus === "RUNNING";
  const isTripFinished = normalizedServiceStatus === "FINISH";
  const bookingStatusLabel = isTripFinished
    ? "FINALIZADO"
    : isTripRunning
      ? "EN VIAJE"
      : isTripPending
        ? "ESPERANDO..."
        : "";
  const bookingStatusColor = isTripFinished
    ? {
        bgcolor: "#e8f5e9",
        color: "#1b5e20",
      }
    : isTripRunning
      ? {
          bgcolor: "#e3f2fd",
          color: "#0d47a1",
        }
      : isTripPending
        ? {
            bgcolor: "#fff7e0",
            color: "#8a5a00",
          }
        : {
            bgcolor: "grey.100",
            color: "text.secondary",
          };
  const serviceStatusLabel = getServiceStatusLabel(
    bookingOfferDetail?.statusService,
  );
  const canRateDriver = isTripRunning;
  const whatsappMessage = encodeURIComponent(
    `*Pedido:* ${bookingOfferDetail?.pedido},\n *Cliente:* ${bookingOfferDetail?.clientName},\n *Origen:* ${bookingOfferDetail?.origen}, *Destino:* ${bookingOfferDetail?.destino} `,
  );
  const whatsappUrl = `https://api.whatsapp.com/send?phone=+51958200900&text=${whatsappMessage}`;

  const handleOpenCancelConfirm = () => {
    setCancelReason("");
    setOpenCancelConfirm(true);
  };

  const handleCloseCancelConfirm = () => {
    setOpenCancelConfirm(false);
    setCancelReason("");
    setIsCancellingBooking(false);
  };

  const handleOpenRatingModal = () => {
    setRatingError("");
    setOpenRatingModal(true);
  };

  const handleCloseRatingModal = () => {
    if (isSubmittingRating) return;
    setOpenRatingModal(false);
    setRatingError("");
  };

  const handleSubmitRating = async (event) => {
    event.preventDefault();

    if (driverRating === 0) {
      setRatingError("Selecciona una calificacion para continuar");
      return;
    }

    const counterBooking = bookingOfferDetail?.counterBooking;
    const driversId = Number(bookingOfferDetail?.idDriver);

    if (!counterBooking || !driversId) {
      setRatingError("No se encontro la informacion del conductor o pedido");
      return;
    }

    const driverCommentPayload = {
      drivers_id: driversId,
      booking_type: "CLIENT",
      booking_id: Number(counterBooking),
      name: bookingOfferDetail?.clientName || acceptedDriverDetail.name || "",
      rating: driverRating,
      comments: driverComment,
    };

    setIsSubmittingRating(true);
    try {
      if (existingDriverCommentId) {
        await updateDriverCommentUseCase({
          id: existingDriverCommentId,
          ...driverCommentPayload,
        });
      } else {
        const response = await createDriverCommentUseCase(driverCommentPayload);
        const createdComment = getResponseData(response);
        setExistingDriverCommentId(createdComment?.id ?? null);
      }
      setRatingError("");
      setOpenRatingModal(false);
    } catch (error) {
      console.log(error);
      setRatingError("No se pudo enviar la calificacion");
    } finally {
      setIsSubmittingRating(false);
    }
  };

  useEffect(() => {
    if (!openRatingModal) return undefined;

    const counterBooking = bookingOfferDetail?.counterBooking;
    if (!counterBooking) {
      setRatingError("No se encontro el pedido para consultar la calificacion");
      return undefined;
    }

    let isMounted = true;

    const loadDriverComment = async () => {
      setIsLoadingRating(true);
      setRatingError("");
      setExistingDriverCommentId(null);
      setDriverRating(0);
      setDriverComment("");

      try {
        const response = await getDriverCommentByBookingUseCase(counterBooking);
        if (!isMounted) return;

        const driverComment = getResponseData(response);
        const commentItem = Array.isArray(driverComment)
          ? driverComment[0]
          : driverComment;

        setExistingDriverCommentId(commentItem?.id ?? null);
        setDriverRating(Number(commentItem?.rating ?? 0));
        setDriverComment(commentItem?.comments ?? "");
      } catch (error) {
        console.log(error);
        if (!isMounted) return;
        setExistingDriverCommentId(null);
        setDriverRating(0);
        setDriverComment("");
        if (error?.response?.status !== 404) {
          setRatingError("No se pudo consultar la calificacion anterior");
        }
      } finally {
        if (isMounted) {
          setIsLoadingRating(false);
        }
      }
    };

    loadDriverComment();

    return () => {
      isMounted = false;
    };
  }, [bookingOfferDetail?.counterBooking, openRatingModal]);

  useEffect(() => {
    const counterBooking = bookingOfferDetail?.counterBooking ?? "";
    const previousServiceStatus = previousServiceStatusRef.current;
    const isSameBooking =
      previousServiceStatus.counterBooking === counterBooking;
    const shouldOpenRatingModal =
      hasAcceptedDriver &&
      isSameBooking &&
      previousServiceStatus.status === "RUNNING" &&
      normalizedServiceStatus === "FINISH";

    if (shouldOpenRatingModal) {
      setRatingError("");
      setOpenRatingModal(true);
    }

    previousServiceStatusRef.current = {
      counterBooking,
      status: normalizedServiceStatus,
    };
  }, [
    bookingOfferDetail?.counterBooking,
    hasAcceptedDriver,
    normalizedServiceStatus,
  ]);

  const handleConfirmCancel = async () => {
    if (!cancelReason || !bookingOfferDetail?.uuid) return;
    const backupOrderId =
      bookingOfferDetail.backupOrderId ??
      bookingOfferDetail.pedido ??
      bookingOfferDetail.counterBooking;

    setIsCancellingBooking(true);
    try {
      await onCancelBooking(
        bookingOfferDetail.uuid,
        cancelReason,
        backupOrderId,
      );
      handleCloseCancelConfirm();
      onClose();
    } catch (error) {
      console.log(error);
    } finally {
      setIsCancellingBooking(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setMainCounterSeconds(0);
      return undefined;
    }

    const updateMainCounter = () => {
      setMainCounterSeconds(
        getElapsedBookingSeconds(
          bookingOfferDetail?.fecha,
          bookingOfferDetail?.hora,
        ),
      );
    };

    updateMainCounter();

    const intervalId = setInterval(() => {
      updateMainCounter();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [bookingOfferDetail?.fecha, bookingOfferDetail?.hora, open]);

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          style={{
            ...modalStyle,
            background: "white",
          }}
          sx={{
            width: {
              xs: "95%",
              sm: "600px",
            },
            outline: "none",
            maxHeight: offersListMaxHeight,
            minHeight: "95vh",
            overflowY: "auto",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              borderRadius: "10px",
              border: "1px solid #000",
              margin: 0.5,
            }}
          >
            <CardContent>
              <Stack spacing={0.5}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  <DetailCol
                    label="Pedido"
                    value={bookingOfferDetail?.pedido}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <DetailCol
                      label="Fecha"
                      value={bookingOfferDetail?.fecha}
                    />
                    <DetailCol label="Hora" value={bookingOfferDetail?.hora} />
                  </Box>
                </Box>

                <Divider />

                {bookingOfferDetail?.otherPersonName ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1,
                      }}
                    >
                      <DetailCol
                        label="Para"
                        value={bookingOfferDetail.otherPersonName}
                      />
                      <DetailCol
                        label="Tel"
                        value={bookingOfferDetail?.otherPersonPhone}
                      />
                    </Box>
                    <Divider />
                  </>
                ) : null}

                <DetailRow label="Origen" value={bookingOfferDetail?.origen} />
                <SubRoutesList subRoutes={bookingOfferDetail?.subRoutes} />
                <DetailRow
                  label="Destino"
                  value={bookingOfferDetail?.destino}
                />
                <DetailRow label="Notas" value={bookingOfferDetail?.notas} />
                <DetailCol
                  label="Precio"
                  value={`${bookingOfferDetail?.total} PEN`}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pt: 1,
                  }}
                >
                  <CounterLabel>
                    {formatMainCounter(mainCounterSeconds)}
                  </CounterLabel>
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Whatsapp
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={handleOpenCancelConfirm}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {hasAcceptedDriver ? (
            <Card
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid #000",
                margin: 0.5,
                mt: 2,
              }}
            >
              <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, color: "#0d7400" }}
                  >
                    Pedido Aceptado
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.35,
                      borderRadius: "6px",
                      bgcolor: bookingStatusColor.bgcolor,
                      color: bookingStatusColor.color,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {bookingStatusLabel}
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <DetailCol
                      label="Conductor"
                      value={
                        serviceStatusLabel
                          ? `${acceptedDriverDetail.name || "-"}`
                          : acceptedDriverDetail.name
                      }
                    />
                    <DetailCol
                      label="Tiempo"
                      value={acceptedDriverDetail.time}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    <DetailCol
                      label="Placa"
                      value={acceptedDriverDetail.plate}
                    />
                    <DetailCol
                      label="Modelo"
                      value={acceptedDriverDetail.model}
                    />
                    <DetailCol
                      label="Color"
                      value={acceptedDriverDetail.color}
                    />
                  </Box>
                  {canRateDriver ? (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        onClick={handleOpenRatingModal}
                      >
                        Calificar Conductor
                      </Button>
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    {isTripPending ? <CarIcon /> : <RouteLabel>A</RouteLabel>}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        overflow: "hidden",
                        "@keyframes moveCarLottie": {
                          "0%": {
                            transform: "translateX(-120px)",
                          },
                          "100%": {
                            transform: "translateX(calc(100% + 120px))",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          animation: "moveCarLottie 8s linear infinite",
                        }}
                      >
                        <CarIcon />
                      </Box>
                      <div
                        style={{
                          position: "absolute",
                          bottom: 12,
                          left: 0,
                          width: "100%",
                          background: "#000",
                          height: "2px",
                        }}
                      ></div>
                    </Box>
                    <RouteLabel>{isTripRunning ? "B" : "A"}</RouteLabel>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", textAlign: "center", mt: 2, mb: 1 }}
              >
                Ofertas de conductores
              </Typography>

              {hasDriverOffers ? (
                <Stack spacing={1} sx={{ p: 0.5 }}>
                  {driverOffers.map((offer, index) => (
                    <OfferCard
                      key={offer.idDriver || offer.idBooking || index}
                      offer={offer}
                      onAccept={onAcceptCounterOffer}
                    />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
                  <RadarAnimation />
                </Box>
              )}
            </>
          )}
        </Box>
      </Modal>

      <Modal open={openRatingModal} onClose={handleCloseRatingModal}>
        <Box
          component="form"
          onSubmit={handleSubmitRating}
          style={{
            ...modalStyle,
            height: "50vh",
            background: "white",
          }}
          sx={{
            width: { xs: "90%", sm: "420px" },
            outline: "none",
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: "8px" }}>
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Calificar Conductor
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleCloseRatingModal}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 1 }}
                  >
                    {isLoadingRating
                      ? "Consultando calificacion anterior..."
                      : "Selecciona una calificacion"}
                  </Typography>
                  <Rating
                    size="large"
                    precision={0.5}
                    disabled={isLoadingRating || isSubmittingRating}
                    value={driverRating}
                    onChange={(_, newValue) => {
                      setDriverRating(newValue || 0);
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Comentario"
                  value={driverComment}
                  disabled={isLoadingRating || isSubmittingRating}
                  onChange={(event) => setDriverComment(event.target.value)}
                />

                {ratingError ? (
                  <Typography variant="body2" sx={{ color: "error.main" }}>
                    {ratingError}
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={isSubmittingRating}
                    onClick={handleCloseRatingModal}
                  >
                    Cerrar
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={
                      driverRating === 0 ||
                      isSubmittingRating ||
                      isLoadingRating
                    }
                  >
                    {isSubmittingRating
                      ? "Enviando..."
                      : existingDriverCommentId
                        ? "Actualizar"
                        : "Enviar"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Modal>

      <Modal open={openCancelConfirm} onClose={handleCloseCancelConfirm}>
        <Box
          style={{
            ...modalStyle,
            background: "white",
          }}
          sx={{
            width: {
              xs: "90%",
              sm: "420px",
            },
            outline: "none",
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: "8px" }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Desea Cancelar este Pedido?
                </Typography>

                <RadioGroup
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                >
                  {cancelReasonOptions.map((reason) => (
                    <FormControlLabel
                      key={reason.id || reason.code || reason.name}
                      value={String(reason.id || reason.code || reason.name)}
                      control={<Radio />}
                      label={reason.name}
                    />
                  ))}
                </RadioGroup>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCloseCancelConfirm}
                  >
                    Cerrar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    disabled={!cancelReason || isCancellingBooking}
                    onClick={handleConfirmCancel}
                  >
                    Si cancelar viaje
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </>
  );
};

export default OffersModal;
